import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import SelectComponent from "@common/components/shared/forms/SelectComponent";
import TextareaComponent from "@common/components/shared/forms/TextareaComponent";
import DiscussionForm from "./DiscussionForm";
import InteractionOptionsAction from "@common/redux/actions/InteractionOptionsAction";
import useLogin from "../../hooks/useLogin";
import { Divider } from "semantic-ui-react";
import { mapToSelectWithOrder } from "@common/components/helpers/forms";
import ToggleComponent from "../forms/ToggleComponent";
import DateComponent from "../forms/DateComponent";
import GuardianClient from "@common/api/GuardianClient";
import { handleError } from "@common/components/helpers/error";

const CustomerOutcomeForm = ({ config = { hasSetVulnerable: true, hasSetTemporaryBreak: true } }) => {
  const {
    loadingOptions,
    customerInteractionOptions: { outcomes },
  } = useSelector((store) => InteractionOptionsAction.getStatePath(store));
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [isVulnerable, outcome, temporaryBreak] = watch([
    "isVulnerable",
    "outcome",
    "temporaryBreak",
  ]);
  const [{ isDesktop, isInReadMode }] = useLogin();

  /**
  This useEffect is to address a validation issue in CMS Desktop for "outcome":
  When isCustomerOk changes, the filtered options change, but the previously
  selected value still lingers and might not exist in the new options list,
  causing the component to appear blank while retaining an invalid value that
  doesn't get validated.
  */
  useEffect(() => {
    if (!isDesktop && !isInReadMode) return;

    if (isVulnerable !== null && outcome) {
      const isCurrentValueValid = mapToSelectWithOrder(outcomes).some(
        (option) => option.value === outcome,
      );

      if (!isCurrentValueValid && !isInReadMode) {
        setValue("outcome", null, { shouldValidate: true });
      }
    }
  }, [isVulnerable, outcome, outcomes, setValue]);

  return (
    <>
      <SelectComponent
        name="outcome"
        label="Outcome"
        placeholder="Select outcome..."
        control={control}
        error={errors.outcome}
        options={mapToSelectWithOrder(outcomes)}
        loading={loadingOptions}
      />
      {outcome === 4 && (
        <TextareaComponent
          name="otherOutcome"
          placeholder="Please describe the outcome"
          register={register}
          error={errors.otherOutcome}
        />
      )}
      {config.hasSetVulnerable && (
        <>
          <Divider />
          <ToggleComponent
            control={control}
            name="isVulnerable"
            label="Flag this customer as vulnerable?"
            fieldStyle="flex-between flex-row-center my-1 pr-1"
            error={errors.isVulnerable}
          />
          {isVulnerable && <DiscussionForm />}
        </>
      )}
      {config.hasSetTemporaryBreak && (
        <>
          <Divider />
          <ToggleComponent
            control={control}
            name="temporaryBreak"
            label="Set up a temporary break for the customer"
            fieldStyle="flex-between flex-row-center my-1 pr-1"
            error={errors.temporaryBreak}
          />
          {temporaryBreak && (
            <DateComponent
              control={control}
              name="endDateOfBreak"
              label="Return date"
              error={errors.endDateOfBreak}
              dateFormat="YYYY-MM-DD"
            />
          )}
        </>
      )}
      <TextareaComponent
        name="comments"
        label="Comments"
        placeholder="Please enter comments..."
        register={register}
        fieldStyle="py-1"
      />
    </>
  );
};

export default CustomerOutcomeForm;
